/* eslint-disable @typescript-eslint/no-explicit-any */
import { AdministrativeScope } from '@/models/enums/AdministrativeScope'
import { BeneficiaryType } from '@/models/enums/contents/BeneficiaryType'
import { ProjectFinancingType } from '@/models/enums/contents/ProjectFinancingType'
import { Status } from '@/models/enums/contents/Status'
import type { Project, ProjectSubmission } from '@/models/interfaces/Project'
import { i18n } from '@/plugins/i18n'
import { toTypedSchema } from '@vee-validate/zod'
import { useField, useForm } from 'vee-validate'
import { z } from 'zod'
import { CommonZodSchema } from '../forms/CommonZodSchema'

export class ProjectFormService {
  static getForm(project: Project | null) {
    const zodModels = CommonZodSchema.getDefinitions()
    // @ts-expect-error wrong zod type
    const projectSchema: z.ZodType<Partial<Project>> = z
      .object({
        name: z.string().min(1, { message: i18n.t('forms.errorMessages.required') }),
        description: zodModels.descriptionRequired,
        calendar: zodModels.description,
        deliverables: zodModels.description,
        focalPointName: z.string().min(1, { message: i18n.t('forms.errorMessages.required') }),
        focalPointPosition: zodModels.maxLabel.optional(),
        focalPointEmail: zodModels.email,
        administrativeScopes: z.array(z.nativeEnum(AdministrativeScope), {
          message: i18n.t('forms.errorMessages.required'),
        }),
        admin1List: zodModels.admin1Boundaries.optional(),
        admin3List: zodModels.admin3Boundaries.optional(),
        focalPointTel: zodModels.phone,
        geoData: zodModels.geoDataNullable.optional(),
        actor: zodModels.symfonyRelation.optional(),
        otherActor: z.string().optional(),
        status: z.nativeEnum(Status, { required_error: i18n.t('forms.errorMessages.required') }),
        financingTypes: z.array(z.nativeEnum(ProjectFinancingType), {
          required_error: i18n.t('forms.errorMessages.required'),
        }),
        otherFinancingType: z.string().optional(),
        fundingAmount: z
          .union([z.string(), z.number()])
          .optional()
          .transform((value) => {
            if (value === undefined || value === null) return undefined
            if (typeof value === 'number' && !Number.isNaN(value)) return String(value)
            const trimmed = String(value).trim()
            return trimmed === '' ? undefined : trimmed
          }),
        actorsInCharge: z.array(zodModels.symfonyRelation).optional(),
        otherActorInCharge: z.string().optional(),
        thematics: zodModels.thematics,
        otherThematic: z.string().optional(),
        odds: zodModels.odds,
        beneficiaryTypes: z.array(z.nativeEnum(BeneficiaryType), {
          required_error: i18n.t('forms.errorMessages.required'),
        }),
        otherBeneficiary: z.string().optional(),
        website: zodModels.website,
        banoc: zodModels.banoc,
        banocUrl: zodModels.banocUrl,
      })
      .refine(
        (data) => {
          return (
            (data.actorsInCharge !== undefined && data.actorsInCharge.length > 0) ||
            !!data.otherActorInCharge
          )
        },
        {
          message: i18n.t('projects.form.errorMessages.noActor'),
          path: ['actorsInCharge', 'otherActorInCharge'],
        },
      )
      .refine(
        (data) => {
          return (data.actor !== undefined && data.actor !== null) || !!data.otherActor
        },
        {
          message: i18n.t('projects.form.errorMessages.noActor'),
          path: ['actor', 'otherActor'],
        },
      )

    const defaultValues: Partial<Project> = {
      name: '',
      description: '',
      deliverables: '',
      calendar: '',
      actorsInCharge: undefined,
      otherActorInCharge: undefined,
      financingTypes: undefined,
      fundingAmount: '',
      administrativeScopes: undefined,
      focalPointName: '',
      focalPointPosition: '',
      focalPointEmail: '',
      focalPointTel: '',
      beneficiaryTypes: undefined,
      actor: undefined,
      otherActor: '',
      status: undefined,
      geoData: undefined,
      thematics: undefined,
      odds: undefined,
      website: '',
      banoc: undefined,
      banocUrl: undefined,
    }

    const fundingAmountInitial =
      project?.fundingAmount != null && String(project.fundingAmount).trim() !== ''
        ? String(project.fundingAmount)
        : ''

    const { errors, handleSubmit, isSubmitting } = useForm<Partial<Project | ProjectSubmission>>({
      initialValues: {
        ...defaultValues,
        ...project,
        fundingAmount: fundingAmountInitial,
      },
      validationSchema: toTypedSchema(projectSchema),
    })

    const form = {
      name: useField<string>('name'),
      description: useField<string>('description'),
      deliverables: useField<string>('deliverables'),
      calendar: useField<string>('calendar'),
      actorsInCharge: useField<(typeof zodModels.symfonyRelation)[]>('actorsInCharge'),
      otherActorInCharge: useField<string>('otherActorInCharge'),
      financingTypes: useField<ProjectFinancingType[]>('financingTypes'),
      otherFinancingType: useField<string>('otherFinancingType'),
      fundingAmount: useField<string>('fundingAmount'),
      administrativeScopes: useField<AdministrativeScope[]>('administrativeScopes'),
      admin1List: useField<typeof zodModels.admin1Boundaries>('admin1List'),
      admin3List: useField<typeof zodModels.admin3Boundaries>('admin3List'),
      focalPointName: useField<string>('focalPointName'),
      focalPointPosition: useField<string>('focalPointPosition'),
      focalPointEmail: useField<string>('focalPointEmail'),
      focalPointTel: useField<string>('focalPointTel'),
      beneficiaryTypes: useField<BeneficiaryType[]>('beneficiaryTypes'),
      otherBeneficiary: useField<string>('otherBeneficiary'),
      actor: useField<typeof zodModels.symfonyRelation>('actor'),
      otherActor: useField<string>('otherActor'),
      status: useField<Status>('status'),
      geoData: useField<any>('geoData'),
      thematics: useField<any>('thematics'),
      otherThematic: useField<string>('otherThematic'),
      odds: useField<typeof zodModels.odds>('odds'),
      website: useField<string>('website'),
      banoc: useField<any>('banoc'),
      banocUrl: useField<any>('banocUrl'),
    }

    return { form, errors, handleSubmit, isSubmitting }
  }
}
